import { forkJoin, Observable, of, OperatorFunction } from 'rxjs';
import { catchError, map, shareReplay, startWith } from 'rxjs/operators';

import { DealPartner, DealPartnerOption, PartnerType, Portfolio, ResponseFromServer } from '../_models/models';
import { PARTNER_TYPE_LABELS, PARTNER_TYPE_TAG_CLASSES } from '../_constants/partner';

export interface PartnerScopeOptions {
  accounts: DealPartner[];
  portfolios: Portfolio[];
}

export function isAgencyType(partnerType: PartnerType | string): boolean {
  return partnerType?.toUpperCase() === PartnerType.AGENCY;
}

export function isAgencyPartner(partner: DealPartner): boolean {
  return isAgencyType(partner?.partnerType);
}

export function getPartnerTypeTagLabel(partner: DealPartner): string {
  if (partner?.isHoldCo) {
    return PARTNER_TYPE_LABELS.HOLD_CO;
  }
  return isAgencyPartner(partner) ? PARTNER_TYPE_LABELS.AGENCY : PARTNER_TYPE_LABELS.CURATOR;
}

export function getPartnerTypeTagClass(partner: DealPartner): string {
  if (partner?.isHoldCo) {
    return PARTNER_TYPE_TAG_CLASSES.HOLD_CO;
  }
  return isAgencyPartner(partner) ? PARTNER_TYPE_TAG_CLASSES.AGENCY : PARTNER_TYPE_TAG_CLASSES.CURATOR;
}

export interface PartnerTypeTag {
  label: string;
  cssClass: string;
}

/** HoldCo / Agency / Curator tag; none when the partner type is unknown. */
export function getPartnerTypeTag(partner: DealPartner): PartnerTypeTag | undefined {
  if (!partner?.isHoldCo && !partner?.partnerType) {
    return undefined;
  }
  return { label: getPartnerTypeTagLabel(partner), cssClass: getPartnerTypeTagClass(partner) };
}

export function getParentAgencyOptions(partner: DealPartner): DealPartnerOption[] {
  return partner?.parentPartnerId
    ? [{ id: partner.parentPartnerId, name: partner.parentPartnerName }]
    : [];
}


export function toListOrEmpty<T>(
  onError: () => Observable<T[]> = () => of([])
): OperatorFunction<ResponseFromServer<T[]>, T[]> {
  return source => source.pipe(
    map(response => response.data ?? []),
    catchError(() => onError())
  );
}

export interface PartnerHierarchySource {
  getDescendantPartners(partnerId: number): Observable<ResponseFromServer<DealPartner[]>>;
  getPortfoliosByHierarchy(partnerId: number): Observable<ResponseFromServer<Portfolio[]>>;
}

export function loadScopeOptions$(
  source: PartnerHierarchySource,
  partnerId: number,
  onError: <T>(subject: string) => Observable<T[]> = () => of([])
): Observable<PartnerScopeOptions> {
  return forkJoin({
    accounts: source.getDescendantPartners(partnerId)
      .pipe(toListOrEmpty<DealPartner>(() => onError('accounts'))),
    portfolios: source.getPortfoliosByHierarchy(partnerId)
      .pipe(toListOrEmpty<Portfolio>(() => onError('portfolios')))
  }).pipe(
    startWith({ accounts: [], portfolios: [] } as PartnerScopeOptions),
    shareReplay(1)
  );
}

export function toChildPartners$(
  accountOptions$: Observable<DealPartner[]>,
  partnerId: number
): Observable<DealPartner[]> {
  return accountOptions$.pipe(
    map(accounts => (accounts ?? []).filter(account => Number(account.id) !== Number(partnerId)))
  );
}
